import {getCopilotSpacePath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotSpace} from '@github-ui/copilot-chat-types'
import {useNavigate} from '@github-ui/use-navigate'
import {Banner, LinkButton} from '@primer/react'

export function SpaceDeletedBanner() {
  return (
    <Banner
      title="Chat is disabled"
      variant="warning"
      description="Conversation unavailable. The linked space may have been deleted, or you might be signed out of your organization."
      hideTitle
    />
  )
}

export function SpaceSizeExceededBanner({customCopilot}: {customCopilot: CopilotSpace | undefined}) {
  const navigate = useNavigate()

  return (
    customCopilot && (
      <Banner
        title="Chat is disabled"
        variant="critical"
        description="You've exceeded the size limit for this space. Remove some references to continue chatting."
        hideTitle
        primaryAction={
          <LinkButton
            as="a"
            href={getCopilotSpacePath(customCopilot)}
            onClick={event => {
              event.preventDefault()
              void navigate(getCopilotSpacePath(customCopilot))
            }}
          >
            Edit
          </LinkButton>
        }
      />
    )
  )
}

export function AssociatedRepositoryReferenceNotFoundBanner() {
  return (
    <Banner
      title="Reference not found"
      variant="critical"
      description="We couldn't access that repository. Make sure it exists and that you have permission to use it."
      hideTitle
      className="tmp-mb-3"
    />
  )
}

interface UploadErrorBannerProps {
  errorMessages: string[]
  onDismiss: () => void
  className?: string
  bannerRef?: React.RefObject<HTMLDivElement | null>
}

export function UploadErrorBanner({errorMessages, onDismiss, className = '', bannerRef}: UploadErrorBannerProps) {
  if (errorMessages.length === 0) return null

  const uniqueErrorMessages = Array.from(new Set(errorMessages))

  const descriptionContent =
    uniqueErrorMessages.length === 1 ? (
      uniqueErrorMessages[0]
    ) : (
      <>
        Errors during attachment upload:
        <ul style={{paddingLeft: '1.2em'}}>
          {uniqueErrorMessages.map(errorMessage => (
            <li key={errorMessage}>{errorMessage}</li>
          ))}
        </ul>
      </>
    )

  return (
    <Banner
      variant="critical"
      description={
        <div ref={bannerRef} tabIndex={-1} style={{outline: 'none'}}>
          {descriptionContent}
        </div>
      }
      title="Error during attachment upload"
      hideTitle
      className={className}
      onDismiss={onDismiss}
    />
  )
}
